import * as THREE from 'three';
import { PX_PER_METRE, fitText } from '../../engine/text.js';
import { REFUSAL_SECONDS } from '../../engine/furnishing.js';

/**
 * A short plate over a piece (ADR-037): why a grab was refused ("Iemand anders heeft dit al
 * vast."), or that the villa is locked, for 1.5 s. World-locked above the piece and turned to face
 * the pupil, never head-locked; white on the app's charcoal, at 1024 px a metre (docs/04).
 */
export const NOTICE = { width: 0.9, height: 0.12, lift: 0.3 } as const;

export class NoticePlate {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas: HTMLCanvasElement;
  readonly #texture: THREE.CanvasTexture;
  readonly #eye = new THREE.Vector3();
  #until = 0;

  constructor() {
    this.#canvas = document.createElement('canvas');
    this.#canvas.width = Math.round(NOTICE.width * PX_PER_METRE);
    this.#canvas.height = Math.round(NOTICE.height * PX_PER_METRE);
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(NOTICE.width, NOTICE.height),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false,
        toneMapped: false,
      }),
    );
    this.mesh.name = 'furniture-notice';
    this.mesh.renderOrder = 40;
    this.mesh.visible = false;
  }

  /** Says `text` at `at` (the piece's top) from `now` (seconds) for 1.5 s. */
  show(text: string, at: THREE.Vector3, now: number): void {
    const ctx = this.#canvas.getContext('2d');
    if (ctx) {
      const { width: w, height: h } = this.#canvas;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#1c1d22';
      ctx.beginPath();
      ctx.roundRect(0, 0, w, h, h / 2);
      ctx.fill();
      const measure = (line: string, size: number, bold: boolean): number => {
        ctx.font = `${bold ? 'bold ' : ''}${size}px system-ui, sans-serif`;
        return ctx.measureText(line).width;
      };
      const fitted = fitText(
        text,
        { maxWidth: w - 60, maxHeight: h - 20, fontSize: 64, minFontSize: 32, bold: true },
        measure,
      );
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `bold ${fitted.fontSize}px system-ui, sans-serif`;
      ctx.fillText(fitted.lines[0] ?? text, w / 2, h / 2);
      this.#texture.needsUpdate = true;
    }
    this.mesh.position.copy(at).add(new THREE.Vector3(0, NOTICE.lift, 0));
    this.mesh.visible = true;
    this.#until = now + REFUSAL_SECONDS;
  }

  /** Each frame: facing the pupil, gone after its 1.5 s. */
  update(now: number, camera: THREE.Camera): void {
    if (!this.mesh.visible) return;
    if (now >= this.#until) {
      this.mesh.visible = false;
      return;
    }
    camera.getWorldPosition(this.#eye);
    this.mesh.rotation.set(
      0,
      Math.atan2(this.#eye.x - this.mesh.position.x, this.#eye.z - this.mesh.position.z),
      0,
    );
  }

  dispose(): void {
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.mesh.removeFromParent();
  }
}
